import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Course, UserSession, BadgeRecord, CourseProgress, resolveAssetUrl, MysteryReward } from '../types';
import { api, progressStore, rewardStore } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import {
  Award,
  BookOpen,
  Calendar,
  CheckCircle,
  Clock,
  ExternalLink,
  Flame,
  Play,
  Shield,
  Sparkles,
  Trophy,
  User as UserIcon,
  Check,
  CheckCircle2,
  Settings,
  Lock,
  Globe,
  Bell,
  Gift,
  Zap,
  FileText
} from 'lucide-react';

interface ProfilePageProps {
  session: UserSession;
}

export const ProfilePage: React.FC<ProfilePageProps> = ({ session }) => {
  const { lang, setLang, t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = (searchParams.get('tab') as 'overview' | 'badges' | 'settings') || 'overview';

  const [courses, setCourses] = useState<Course[]>([]);
  const [badges, setBadges] = useState<BadgeRecord[]>([]);
  const [rewards, setRewards] = useState<MysteryReward[]>([]);
  const [progressMap, setProgressMap] = useState<Record<string, CourseProgress>>({});
  const [overallStats, setOverallStats] = useState({ completedCourses: 0, totalCourses: 5, overallPercent: 0 });
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    Promise.all([
      api.getCourses(),
      Promise.resolve(progressStore.getBadges(session.userId)),
      Promise.resolve(rewardStore.getRewards(session.userId)),
    ]).then(([courseData, badgeData, rewardData]) => {
      setCourses(courseData);
      setBadges(badgeData);
      setRewards(rewardData);

      const pMap: Record<string, CourseProgress> = {};
      for (const c of courseData) {
        pMap[c.courseId] = progressStore.getCourseProgress(session.userId, c.courseId);
      }
      setProgressMap(pMap);
      setOverallStats(progressStore.getOverallProgress(session.userId, courseData.length || 5));
      setLoading(false);
    });
  }, [session.userId]);

  const initials = session.email ? session.email.substring(0, 2).toUpperCase() : 'US';
  const totalCourses = courses.length || 5;
  const completedCount = badges.length;
  const overallCompliance = overallStats.overallPercent;

  let masteryLevel = t('Iniciando Ruta', 'Apprentice');
  let masteryBadgeColor = '#94a3b8';
  if (completedCount === totalCourses) {
    masteryLevel = t('Líder de Cumplimiento Total', 'Master Compliance Officer');
    masteryBadgeColor = '#f59e0b';
  } else if (completedCount >= 3) {
    masteryLevel = t('Especialista Avanzado', 'Senior Specialist');
    masteryBadgeColor = '#3b82f6';
  } else if (completedCount >= 1) {
    masteryLevel = t('Practicante Certificado', 'Certified Practitioner');
    masteryBadgeColor = '#10b981';
  }

  if (loading) {
    return (
      <div style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Sparkles className="spin-slow" size={32} color="#3b82f6" />
      </div>
    );
  }

  return (
    <div style={{ padding: '36px 24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header Profile Hero Card */}
      <div
        className="glass-card"
        style={{
          padding: '28px 32px',
          borderRadius: 'var(--radius-lg)',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '24px',
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.9) 100%)',
          border: '1px solid var(--border-color)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div
            style={{
              width: '68px',
              height: '68px',
              borderRadius: '20px',
              background: 'var(--gradient-brand)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              fontSize: '24px',
              fontWeight: 900,
              boxShadow: 'var(--shadow-glow)',
            }}
          >
            {initials}
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              <h1 style={{ fontSize: '22px', fontWeight: 800, margin: 0, color: 'var(--text-primary)' }}>
                {session.email}
              </h1>
              <span
                style={{
                  background: 'rgba(59, 130, 246, 0.18)',
                  color: '#60a5fa',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  fontSize: '11px',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '12px',
                }}
              >
                {session.role}
              </span>
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{session.companyId ? `Organización: ${session.companyId}` : t('Modo Individual (B2C)', 'Individual Learner (B2C)')}</span>
              <span>•</span>
              <span style={{ color: masteryBadgeColor, fontWeight: 700 }}>{masteryLevel}</span>
            </div>
          </div>
        </div>

        {/* Global Compliance Widget */}
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center', padding: '10px 18px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
            <div style={{ fontSize: '22px', fontWeight: 900, color: 'var(--accent-primary)' }}>
              {completedCount} / {totalCourses}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>
              {t('Insignias Obtenidas', 'Badges Earned')}
            </div>
          </div>

          <div style={{ textAlign: 'center', padding: '10px 18px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
            <div style={{ fontSize: '22px', fontWeight: 900, color: overallCompliance >= 80 ? '#34d399' : '#f59e0b' }}>
              {overallCompliance}%
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>
              {t('Progreso Global', 'Overall Progress')}
            </div>
          </div>
        </div>
      </div>

      {/* Clean Modern 3-Tab Bar (Mi Perfil / Mis Insignias / Configuración) */}
      <div
        style={{
          display: 'flex',
          gap: '10px',
          borderBottom: '1px solid var(--border-color)',
          marginBottom: '28px',
          paddingBottom: '2px',
        }}
      >
        <button
          onClick={() => setSearchParams({ tab: 'overview' })}
          style={{
            padding: '10px 18px',
            background: activeTab === 'overview' ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
            border: 'none',
            borderBottom: activeTab === 'overview' ? '2px solid #3b82f6' : '2px solid transparent',
            color: activeTab === 'overview' ? '#60a5fa' : 'var(--text-secondary)',
            fontWeight: activeTab === 'overview' ? 800 : 600,
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.15s ease',
          }}
        >
          <UserIcon size={16} />
          {t('Mi Perfil & Avance', 'Profile & Progress')}
        </button>

        <button
          onClick={() => setSearchParams({ tab: 'badges' })}
          style={{
            padding: '10px 18px',
            background: activeTab === 'badges' ? 'rgba(245, 158, 11, 0.15)' : 'transparent',
            border: 'none',
            borderBottom: activeTab === 'badges' ? '2px solid #f59e0b' : '2px solid transparent',
            color: activeTab === 'badges' ? '#fbbf24' : 'var(--text-secondary)',
            fontWeight: activeTab === 'badges' ? 800 : 600,
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.15s ease',
          }}
        >
          <Award size={16} />
          {t('Mis Insignias & Recompensas', 'My Badges & Rewards')}
          {badges.length > 0 && (
            <span style={{ background: 'rgba(245, 158, 11, 0.25)', color: '#fbbf24', fontSize: '11px', padding: '1px 7px', borderRadius: '10px', fontWeight: 800 }}>
              {badges.length}
            </span>
          )}
        </button>

        <button
          onClick={() => setSearchParams({ tab: 'settings' })}
          style={{
            padding: '10px 18px',
            background: activeTab === 'settings' ? 'rgba(168, 85, 247, 0.15)' : 'transparent',
            border: 'none',
            borderBottom: activeTab === 'settings' ? '2px solid #a855f7' : '2px solid transparent',
            color: activeTab === 'settings' ? '#c084fc' : 'var(--text-secondary)',
            fontWeight: activeTab === 'settings' ? 800 : 600,
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.15s ease',
          }}
        >
          <Settings size={16} />
          {t('Configuración', 'Settings')}
        </button>
      </div>

      {/* TAB 1: OVERVIEW & 5-COURSE COMPLIANCE TRACKER TABLE */}
      {activeTab === 'overview' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <BookOpen size={22} color="#3b82f6" />
            <h2 style={{ fontSize: '20px', fontWeight: 800, margin: 0 }}>
              {t('Estado del Plan de Capacitación Obligatorio', 'Mandatory Compliance Training Status')}
            </h2>
          </div>

          <div className="glass-card" style={{ padding: '0', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)', fontSize: '12px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                    <th style={{ padding: '16px 20px' }}>{t('Curso', 'Course')}</th>
                    <th style={{ padding: '16px 20px' }}>{t('Jurisdicción', 'Jurisdiction')}</th>
                    <th style={{ padding: '16px 20px' }}>{t('Progreso & Barra', 'Progress Bar')}</th>
                    <th style={{ padding: '16px 20px' }}>{t('Estado', 'Status')}</th>
                    <th style={{ padding: '16px 20px', textAlign: 'right' }}>{t('Acción', 'Action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.map((c) => {
                    const hasBadge = badges.some((b) => b.courseId === c.courseId);
                    const cp = progressMap[c.courseId];
                    const coursePercent = cp?.progressPercent ?? (hasBadge ? 100 : 0);
                    const status = cp?.status ?? (hasBadge ? 'COMPLETED' : 'NOT_STARTED');

                    return (
                      <tr key={c.courseId} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '16px 20px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {resolveAssetUrl(c.badgeUrl, lang) && (
                              <img src={resolveAssetUrl(c.badgeUrl, lang)} alt="" style={{ width: '36px', height: '36px', objectFit: 'contain' }} />
                            )}
                            <div>
                              <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)' }}>
                                {c.title[lang]}
                              </div>
                              <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>
                                {c.category}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td style={{ padding: '16px 20px', fontSize: '12.5px', color: 'var(--text-secondary)' }}>
                          {c.jurisdiction}
                        </td>

                        <td style={{ padding: '16px 20px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '120px', height: '8px', background: 'var(--bg-secondary)', borderRadius: '4px', overflow: 'hidden' }}>
                              <div style={{ width: `${coursePercent}%`, height: '100%', background: status === 'COMPLETED' ? '#10b981' : 'var(--accent-primary)', transition: 'width 0.3s ease' }} />
                            </div>
                            <span style={{ fontSize: '12px', color: status === 'COMPLETED' ? '#34d399' : 'var(--text-primary)', fontWeight: 700 }}>
                              {coursePercent}%
                            </span>
                          </div>
                        </td>

                        <td style={{ padding: '16px 20px' }}>
                          {status === 'COMPLETED' ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '12px', background: 'rgba(16, 185, 129, 0.15)', color: '#34d399', fontSize: '11.5px', fontWeight: 700 }}>
                              <CheckCircle2 size={13} /> {t('Completado (100%)', 'Completed (100%)')}
                            </span>
                          ) : status === 'IN_PROGRESS' ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '12px', background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', fontSize: '11.5px', fontWeight: 700 }}>
                              <Clock size={13} /> {t('En Progreso', 'In Progress')}
                            </span>
                          ) : (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '12px', background: 'var(--bg-secondary)', color: 'var(--text-muted)', fontSize: '11.5px', fontWeight: 600 }}>
                              {t('No Iniciado', 'Not Started')}
                            </span>
                          )}
                        </td>

                        <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                          <button
                            className={`btn ${status === 'COMPLETED' ? 'btn-secondary' : 'btn-primary'}`}
                            style={{ padding: '6px 14px', fontSize: '12px' }}
                            onClick={() => navigate(`/course/${c.courseId}`)}
                          >
                            {status === 'COMPLETED' ? t('Ver Curso', 'View Course') : status === 'IN_PROGRESS' ? t('Continuar', 'Resume') : t('Iniciar', 'Start')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: BADGES & UNLOCKED REWARDS HUB */}
      {activeTab === 'badges' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Trophy size={22} color="#f59e0b" />
              <h2 style={{ fontSize: '20px', fontWeight: 800, margin: 0 }}>
                {t('Mis Insignias Criptográficas (Open Badges 3.0)', 'My Cryptographic Badges (Open Badges 3.0)')}
              </h2>
            </div>
            <span style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
              {badges.length} {t('credenciales verificables', 'verifiable credentials')}
            </span>
          </div>

          {badges.length === 0 ? (
            <div
              className="glass-card"
              style={{
                padding: '40px',
                textAlign: 'center',
                borderRadius: 'var(--radius-lg)',
                border: '1px dashed var(--border-color)',
                marginBottom: '36px',
              }}
            >
              <Award size={48} color="#94a3b8" style={{ margin: '0 auto 16px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '8px' }}>
                {t('Aún no has emitido insignias', 'No badges earned yet')}
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', maxWidth: '540px', margin: '0 auto 20px', lineHeight: 1.5 }}>
                {t(
                  'Completa los módulos de cualquiera de los 5 cursos obligatorios y aprueba la evaluación oficial (80%+) para acuñar tu primera credencial criptográfica.',
                  'Complete any of the 5 mandatory compliance courses and pass the 12-question evaluation to mint your first cryptographic credential.'
                )}
              </p>
              <button className="btn btn-primary" onClick={() => navigate('/')}>
                <Play size={16} /> {t('Explorar Cursos', 'Explore Courses')}
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '24px', marginBottom: '36px' }}>
              {badges.map((b) => {
                const matchedCourse = courses.find((c) => c.courseId === b.courseId);
                const badgeImage = resolveAssetUrl(b.badgeUrl || matchedCourse?.badgeUrl, lang) || '/assets/badges/badge_sgsst.svg';
                const issuedDate = new Date(b.issuedOn).toLocaleDateString();
                const validUntilDate = new Date(b.validUntil).toLocaleDateString();

                return (
                  <div
                    key={b.badgeId}
                    className="glass-card"
                    style={{
                      padding: '24px',
                      borderRadius: 'var(--radius-lg)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.06) 0%, rgba(15, 23, 42, 0.8) 100%)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                        <img
                          src={badgeImage}
                          alt="Badge"
                          style={{
                            width: '64px',
                            height: '64px',
                            objectFit: 'contain',
                            filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.5))',
                          }}
                        />
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: '#34d399', textTransform: 'uppercase' }}>
                            Open Badges 3.0 • {b.score}%
                          </div>
                          <h4 style={{ fontSize: '15px', fontWeight: 700, margin: '2px 0 4px', color: 'var(--text-primary)' }}>
                            {b.courseTitle}
                          </h4>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                            ID: <code style={{ color: '#93c5fd' }}>{b.badgeId}</code>
                          </div>
                        </div>
                      </div>

                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '18px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Calendar size={13} color="#94a3b8" />
                          <span>{t('Emitido:', 'Issued:')} {issuedDate}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Shield size={13} color="#34d399" />
                          <span>{t('Válido hasta:', 'Valid until:')} {validUntilDate} (365 {t('días', 'days')})</span>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        className="btn btn-primary"
                        style={{ flex: 1, padding: '8px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                        onClick={() => navigate(`/verify/${b.badgeId}`)}
                      >
                        <ExternalLink size={14} />
                        {t('Verificar Credencial', 'Verify Credential')}
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '8px 12px', fontSize: '12px' }}
                        onClick={() => navigate(`/course/${b.courseId}?tab=exam`)}
                      >
                        {t('Repasar', 'Review')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Unlocked Mystery Loot Box Rewards Section */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <Gift size={20} color="#f59e0b" />
              <h3 style={{ fontSize: '18px', fontWeight: 800, margin: 0 }}>
                {t('Cajas Misteriosas & Recompensas Especiales Obtenidas', 'Mystery Boxes & Rewards Unlocked')}
              </h3>
            </div>

            {rewards.length === 0 ? (
              <div className="glass-card" style={{ padding: '24px', borderRadius: 'var(--radius-md)', color: 'var(--text-muted)', fontSize: '13.5px' }}>
                {t('Completa los simuladores de crisis en cualquiera de los cursos para desbloquear cajas misteriosas con kits editables e insignias holográficas.', 'Complete crisis simulators in any course to unlock mystery loot boxes with editable toolkits and holographic skins.')}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                {rewards.map((r) => (
                  <div key={r.id} className="glass-card" style={{ padding: '18px', borderRadius: 'var(--radius-md)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '10px', fontWeight: 900, color: '#fbbf24', textTransform: 'uppercase' }}>{r.rarity}</span>
                      <Sparkles size={14} color="#f59e0b" />
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>
                      {r.title[lang]}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px' }}>
                      {r.description[lang]}
                    </div>
                    <code style={{ fontSize: '11px', color: '#38bdf8', background: 'rgba(56, 189, 248, 0.1)', padding: '2px 6px', borderRadius: '4px' }}>
                      {r.value}
                    </code>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 3: ACCOUNT & SECURITY SETTINGS */}
      {activeTab === 'settings' && (
        <div style={{ maxWidth: '680px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <Settings size={22} color="#a855f7" />
            <h2 style={{ fontSize: '20px', fontWeight: 800, margin: 0 }}>
              {t('Configuración de Cuenta & Preferencias', 'Account Settings & Preferences')}
            </h2>
          </div>

          <div className="glass-card" style={{ padding: '24px', borderRadius: 'var(--radius-lg)', marginBottom: '24px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 700, marginBottom: '16px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <UserIcon size={18} color="#60a5fa" />
              {t('Información Personal & Organización', 'Personal Info & Organization')}
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>{t('Correo Electrónico', 'Email Address')}</label>
                <input type="text" readOnly value={session.email} style={{ width: '100%', padding: '10px', borderRadius: 'var(--radius-sm)', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', fontSize: '13.5px' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>{t('Rol de Cuenta', 'Account Role')}</label>
                <input type="text" readOnly value={session.role} style={{ width: '100%', padding: '10px', borderRadius: 'var(--radius-sm)', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', fontSize: '13.5px' }} />
              </div>
            </div>
          </div>

          <div className="glass-card" style={{ padding: '24px', borderRadius: 'var(--radius-lg)', marginBottom: '24px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 700, marginBottom: '16px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Globe size={18} color="#34d399" />
              {t('Idioma y Preferencias de Interfaz', 'Language & Interface Preferences')}
            </h3>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>{t('Idioma de la Plataforma', 'Platform Language')}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{t('Cambia el idioma de los módulos, insignias y simuladores.', 'Switch language across lessons, badges, and simulators.')}</div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className={`btn ${lang === 'es' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setLang('es')}
                >
                  Español (ES)
                </button>
                <button
                  className={`btn ${lang === 'en' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setLang('en')}
                >
                  English (EN)
                </button>
              </div>
            </div>
          </div>

          <div className="glass-card" style={{ padding: '24px', borderRadius: 'var(--radius-lg)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 700, marginBottom: '16px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Lock size={18} color="#f87171" />
              {t('Seguridad y Autenticación OTP', 'Security & OTP Authentication')}
            </h3>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {t('Tu cuenta utiliza autenticación de dos factores OTP vía WhatsApp o Email de alta seguridad.', 'Your account is secured via high-assurance WhatsApp and Email OTP authentication.')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
